import { type RefObject, useCallback, useEffect } from 'react';
import { Swiper as SwiperType } from 'swiper/types';
import { CAROUSEL_MEDIA_SELECTOR } from '@components/carousel/carouselContract';

export const CHEVRON_HEIGHT_CSS_VAR = '--vds-carousel-chevron-height';

export interface UseChevronTileAlignOptions {
  enabled: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  swiperRef: RefObject<SwiperType | null>;
  isSwiperReady: boolean;
  selector?: string;
}

const queryFirstHTMLElement = (scope: ParentNode, selector: string): HTMLElement | null => {
  try {
    const match = scope.querySelector(selector);
    return match instanceof HTMLElement ? match : null;
  } catch {
    return null;
  }
};

export function findChevronAlignTarget(
  swiperEl: HTMLElement,
  selector: string,
): HTMLElement | null {
  if (!selector.trim()) return null;

  const scopedVisible = queryFirstHTMLElement(swiperEl, `.swiper-slide-visible ${selector}`);
  if (scopedVisible) return scopedVisible;

  const scopedActive = queryFirstHTMLElement(swiperEl, `.swiper-slide-active ${selector}`);
  if (scopedActive) return scopedActive;

  return null;
}

export function updateChevronAlignHeight(
  container: HTMLElement,
  swiperEl: HTMLElement,
  selector: string,
): void {
  const target = findChevronAlignTarget(swiperEl, selector);
  if (!target) {
    container.style.removeProperty(CHEVRON_HEIGHT_CSS_VAR);
    return;
  }

  const height = target.offsetHeight;
  if (height <= 0) {
    container.style.removeProperty(CHEVRON_HEIGHT_CSS_VAR);
    return;
  }

  container.style.setProperty(CHEVRON_HEIGHT_CSS_VAR, `${height}px`);
}

/**
 * Measures the slide media element and sets `--vds-carousel-chevron-height` on the
 * carousel container so navigation chevrons center on the image area instead of the
 * full slide (media + caption).
 */
export function useChevronTileAlign({
  enabled,
  containerRef,
  swiperRef,
  isSwiperReady,
  selector = CAROUSEL_MEDIA_SELECTOR,
}: UseChevronTileAlignOptions): void {
  const measure = useCallback(() => {
    const container = containerRef.current;
    const swiperEl = swiperRef.current?.el as HTMLElement | undefined;
    if (!container || !swiperEl) return;
    updateChevronAlignHeight(container, swiperEl, selector);
  }, [containerRef, swiperRef, selector]);

  useEffect(() => {
    if (!enabled || !isSwiperReady) return;

    const container = containerRef.current;
    const swiper = swiperRef.current;
    if (!container || !swiper) return;

    measure();

    const ResizeObserverCtor =
      typeof globalThis.ResizeObserver === 'function' ? globalThis.ResizeObserver : undefined;
    const observer = ResizeObserverCtor
      ? new ResizeObserverCtor(() => {
          measure();
        })
      : undefined;

    if (observer) {
      observer.observe(container);

      const swiperEl = swiper.el as HTMLElement | undefined;
      if (swiperEl) {
        observer.observe(swiperEl);
      }
    }

    swiper.on('slideChange', measure);
    swiper.on('resize', measure);

    return () => {
      observer?.disconnect();
      swiper.off('slideChange', measure);
      swiper.off('resize', measure);
      container.style.removeProperty(CHEVRON_HEIGHT_CSS_VAR);
    };
  }, [enabled, isSwiperReady, containerRef, swiperRef, measure]);
}
